import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { ApiError, ClientService } from '/src/api';
import { BreadcrumbNav, ListPage, PageHeader, UserList } from '/src/components';
import { config } from '/src/config';
import { useGlobalStore, useUserStore } from '/src/context';
import { useProductTour } from '/src/hooks/useProductTour';
import { TClientId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { getPageTitle } from '/src/utils';
import { usersTour } from '/src/utils/productTours';

export const ListUsersPage = observer(() => {
  /* Context */
  const { user: currentUser, isAdminUser, isInternalUser, isFacilityUser } = useUserStore();
  const { handleApiError } = useGlobalStore();
  const params = useParams();
  const clientId = useMemo(() => parseInt(params.client_id ?? ''), [params.client_id]);
  const { t } = useTranslation();

  /* State */
  const [clientName, setClientName] = useState('');

  /* Computed */
  const isLoading = clientId ? !clientName : false;

  /* Memos */
  const addButtonUrl = useMemo(() => (clientId ? `/clients/${clientId}/users/create` : '/users/create'), [clientId]);

  const breadcrumbNav = useMemo(
    () =>
      clientId && clientName ? (
        <BreadcrumbNav
          previousPages={[
            { name: 'Clients', link: isInternalUser ? '/clients' : undefined },
            { name: clientName, link: !isFacilityUser ? `/clients/${clientId}` : undefined },
          ]}
          currentPageName="Users"
        />
      ) : undefined,
    [clientId, clientName, isInternalUser, isFacilityUser]
  );

  /* Methods */
  const fetchPageData = useCallback(
    async (clientId: TClientId) => {
      if (!currentUser) return;

      if (isFacilityUser) {
        setClientName(currentUser.clients.find((client) => client.id === clientId)?.name ?? '');
      } else {
        try {
          const client = await ClientService.get({ id: clientId });
          setClientName(client.name);
        } catch (err) {
          handleApiError(err as ApiError, toastMessages.listUsers.error);
        }
      }
    },
    [currentUser, isFacilityUser]
  );

  /* Effects */
  useEffect(() => {
    document.title = getPageTitle(config.client.basePageTitle, 'Users');
  }, []);

  useEffect(() => {
    if (clientId && !clientName) {
      fetchPageData(clientId);
    }
  }, [clientId, clientName]);

  /* Product Tour */
  useProductTour({
    tourName: 'users-tour',
    steps: usersTour,
    autoStart: true,
    showOnce: true,
  });

  /* Render */
  return (
    <ListPage
      title={t('user:users')}
      breadcrumbNav={breadcrumbNav}
      pageHeader={
        <PageHeader
          title={t('user:users')}
          showAddButton={isAdminUser && !isFacilityUser}
          addButtonLabel={t('user:addUser')}
          addButtonUrl={addButtonUrl}
          dataTour="add-user-button"
        />
      }
      isLoading={isLoading}
    >
      <UserList showSearchInput showFilters clientId={clientId} standalone data-tour="users-list" />
    </ListPage>
  );
});
